import type { JSX } from 'react'

import { cn } from '../lib/cn.ts'
import {
  queryActivityText,
  type QueryActivitySnapshot
} from '../lib/query-activity.ts'
import { Icon } from './icon.tsx'

export interface QueryProgressProps {
  'data-testid': string
  /** The batch to draw, from `useQueryActivity`. */
  activity: QueryActivitySnapshot
}

/**
 * QueryProgress — the app's one "Querying the datalake" indicator: a thin bar
 * across the top of the window and a status pill at its right.
 *
 * It is fixed to the window rather than to the shell, because the desktop
 * console has no top bar, and it ignores the pointer so it never covers a
 * control. It draws nothing while the batch is not visible. The fill is an
 * estimate and the label never prints a percentage. The fill is ink, not
 * magenta: magenta marks the one primary action (ADR-0016).
 *
 * Mount it once. Pages carry no code for it.
 *
 * @param props - QueryProgress props.
 * @param props.activity - The batch to draw.
 * @returns The indicator, or null when there is nothing to show.
 */
export function QueryProgress({
  'data-testid': testId,
  activity
}: QueryProgressProps): JSX.Element | null {
  if (!activity.visible) return null
  const { label, elapsed, valueText } = queryActivityText(activity)
  const failed = activity.phase === 'failed'
  const running = activity.phase === 'running'

  return (
    <div
      data-testid={testId}
      data-phase={activity.phase}
      data-screenshot-ignore
      data-html2canvas-ignore
      role="progressbar"
      aria-label="Datalake query progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={valueText}
      aria-live="polite"
      className={cn(
        'pointer-events-none fixed inset-x-0 top-0 z-50 transition-opacity duration-300 ease-out motion-reduce:transition-none print:hidden',
        activity.fading ? 'opacity-0' : 'opacity-100'
      )}
    >
      <div className="bg-sunk h-1 w-full">
        <div
          data-testid={`${testId}-fill`}
          className={cn(
            'h-full transition-[width] duration-1000 ease-out motion-reduce:transition-none',
            failed ? 'bg-danger' : 'bg-foreground'
          )}
          style={{ width: `${Math.round(activity.fill * 100)}%` }}
        />
      </div>
      <div
        data-testid={`${testId}-pill`}
        className="bg-popover text-popover-foreground border-rule absolute top-2 right-4 flex items-center gap-2 border-(length:--rule-width) px-3 py-1.5 text-sm"
      >
        {failed ? (
          <Icon
            data-testid={`${testId}-icon-failed`}
            name="circle-x"
            className="text-destructive size-4"
          />
        ) : running ? (
          <Icon
            data-testid={`${testId}-icon-running`}
            name="loader"
            className="size-4 animate-spin motion-reduce:animate-none"
          />
        ) : (
          <Icon
            data-testid={`${testId}-icon-loaded`}
            name="check"
            className="size-4"
          />
        )}
        <span data-testid={`${testId}-label`}>{label}</span>
        {running ? (
          <span
            data-testid={`${testId}-elapsed`}
            aria-hidden="true"
            className="text-muted-foreground font-mono tabular-nums"
          >
            {elapsed}
          </span>
        ) : null}
        {activity.slow && running ? (
          <span
            data-testid={`${testId}-slow`}
            className="text-muted-foreground"
          >
            {'Still working, large ranges take longer'}
          </span>
        ) : null}
      </div>
    </div>
  )
}
